import Axios from "../utils/Axios";
import { isAxiosError } from "axios";

export interface Option {
  value: string;
  label: string;
  points?: number;
}
export interface PartOption {
  value: number;
  label: string;
}
export interface LocationOption {
  value: number;
  label: string;
  color: string;
}
export interface Tool {
  id: number;
  type: string;
  operation: string;
  code: string;
  location_id: number | null;
  location: LocationOption | null;
  details: string;
  is_deleted: boolean;
  parts: {
    part_id: number;
    part_name: string;
    drawing_link?: string | null;
    drawing_name?: string | null;
  }[];
}
export interface Maker {
  id: number;
  name: string;
  color: string;
}
export interface Card {
  id: number;
  tool_id: number;
  tool: Tool;
  issue_type: string;
  points: number;
  detail: string;
  status: string;
  version: number;
  approved: boolean;
  approved_by: string | null;
  approved_at: string | null;
  deletion_pending: boolean;
  is_deleted: boolean;
  makers: Maker[];
  created_at: string;
  completed_at: string | null;
}
export interface ToolOptions {
  types: Option[];
  issues: Option[];
  statuses: Option[];
  parts: PartOption[];
  locations: LocationOption[];
}
export interface Performance {
  makers: (Maker & { points: number; completed_cards: number })[];
  months: { month: string; points: number }[];
  total_points: number;
  completed_cards: number;
  allocation: string;
}
export type CardInput = {
  tool_id: number;
  issue_type: string;
  points: number;
  detail: string;
  status: string;
  maker_ids: number[];
  version?: number;
};
export const getToolOptions = async (): Promise<ToolOptions> =>
  (await Axios.get("/tooling/options")).data;
export const getTools = async (includeDeleted = false): Promise<Tool[]> =>
  (
    await Axios.get("/tooling/tools", {
      params: { include_deleted: includeDeleted },
    })
  ).data;
export const getMakers = async (): Promise<Maker[]> =>
  (await Axios.get("/tooling/makers")).data;
export const getCards = async (
  archived = false,
  deleted = false
): Promise<Card[]> =>
  (await Axios.get("/tooling/cards", { params: { archived, deleted } })).data;
export const saveTool = async (
  data: {
    type: string;
    operation: string;
    part_ids: number[];
    details: string;
    location_id: number | null;
  },
  id?: number
) =>
  (
    await (id
      ? Axios.put(`/tooling/tools/${id}`, data)
      : Axios.post("/tooling/tools", data))
  ).data;
export const saveMaker = async (
  data: { name: string; color: string },
  id?: number
) =>
  (
    await (id
      ? Axios.put(`/tooling/makers/${id}`, data)
      : Axios.post("/tooling/makers", data))
  ).data;
export const saveCard = async (data: CardInput, id?: number): Promise<Card> =>
  (
    await (id
      ? Axios.put(`/tooling/cards/${id}`, data)
      : Axios.post("/tooling/cards", data))
  ).data;
export const approveCard = async (card: Card): Promise<Card> =>
  (
    await Axios.post(`/tooling/cards/${card.id}/approve`, {
      version: card.version,
    })
  ).data;
export const deleteCard = async (card: Card): Promise<Card> =>
  (
    await Axios.delete(`/tooling/cards/${card.id}`, {
      data: { version: card.version },
    })
  ).data;
export const rejectCardDeletion = async (card: Card): Promise<Card> =>
  (
    await Axios.post(`/tooling/cards/${card.id}/reject-deletion`, {
      version: card.version,
    })
  ).data;
export const deleteTool = async (id: number): Promise<Tool> =>
  (await Axios.delete(`/tooling/tools/${id}`)).data;
export const getToolHistory = async (id: number): Promise<Card[]> =>
  (await Axios.get(`/tooling/tools/${id}/history`)).data;
export const getPerformance = async (
  start: string,
  end: string
): Promise<Performance> =>
  (await Axios.get("/tooling/performance", { params: { start, end } })).data;
export const getCardEvents = async (
  id: number
): Promise<
  { id: number; actor_name: string; created_at: string; snapshot: Card }[]
> => (await Axios.get(`/tooling/cards/${id}/events`)).data;
export const toolingError = (error: unknown): string => {
  if (isAxiosError(error)) {
    const message = error.response?.data?.error;
    return typeof message === "string" ? message : error.message;
  }
  return error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";
};
export const cardInput = (card: Card): CardInput => ({
  tool_id: card.tool_id,
  issue_type: card.issue_type,
  points: card.points,
  detail: card.detail,
  status: card.status,
  maker_ids: card.makers.map((m) => m.id),
  version: card.version,
});
export const toolLabel = (tool: Tool) =>
  `${tool.code} · ${tool.parts[0]?.part_name || "No linked part"} (ID ${
    tool.id
  })`;
